import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import {
  fetchTransferHistorys as OnfetchTransferHistorys,
  exportTransferHistoryCSV,
} from "slices/supervisor/stock/transferHistory/thunk";
import WarehouseTableContainer from "Common/WarehouseTableContainer";
import { fetchInStockItems } from "slices/Warehouse/ProjectWarehouse/InStockItem/thunk";
import { fetchWarehouseDropdown } from "slices/Dropdowns/Warehouse/WarehouseDropdown/thunk";

const InStockItemsDetailsListings: React.FC = () => {
  document.title = "In Stock Items List";
  const dispatch = useDispatch<any>();
  const { state } = useLocation();

  const selectInStockItems = createSelector(
    (state: any) => state.inStockItems,
    (pkg) => ({
      InStockItems: pkg?.list || [],
      rowCount: pkg?.rowCount || 0,
      loading: pkg?.loading || false,
      error: pkg?.error || null,
    })
  );

  const { InStockItems, rowCount, loading, error } =
    useSelector(selectInStockItems);

  const { warehouseDropdownList, warehouseDropdownLoading, selectedWarehouse } =
    useSelector(
      createSelector(
        (state: any) => state.warehouseDropdownReducer,
        (data) => ({
          warehouseDropdownList: data.list,
          warehouseDropdownLoading: data.loading,
          warehouseDropdownError: data.error,
          selectedWarehouse: data.selected,
        })
      )
    );

  const user = useSelector((state: any) => state.Login.user);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [colValue, setColValue] = useState<any>();
  const [totalRecords, setTotalRecords] = useState(rowCount);
  const navigate = useNavigate();
  const { id } = useParams();

  const [editInStockItem, setEditInStockItem] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const location = useLocation();
  const currentPath = location.pathname;

  useEffect(() => {
    // if (!state) return;
    // if (!selectedWarehouse?.warehouse?.warehouse_id) return;
    if (!id) return;
    dispatch(
      fetchInStockItems({
        product_id: id,
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,

        warehouse_id: selectedWarehouse?.warehouse?.warehouse_id,
      })
    );
  }, [
    dispatch,
    id,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    selectedWarehouse?.warehouse?.warehouse_id,
    warehouseDropdownList,
  ]);

  useEffect(() => {
    setPageIndex(0);
  }, [sorting, pageSize, columnFilters]);

  useEffect(() => {
    setData(InStockItems);
  }, [InStockItems]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
      setPageIndex(0);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    dispatch(fetchWarehouseDropdown());
  }, [dispatch]);

  // Handle edit action
  const handleEdit = (item: any) => {
    setEditInStockItem(!editInStockItem);
    setEdit({
      id: item.id,
      itemName: item.item_name,
      unit: item.unit,
      quantity: item.quantity,
      status: item.is_active,
      createdBy: item.created_by,
      updatedBy: item.updated_by,
      createDate: item.created_at,
      updatedDate: item.updated_at,
    });
  };

  const handleExport = async () => {
    try {
      const { blob, filename } = await exportTransferHistoryCSV();

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
      alert("Export failed");
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Item Name",
        accessorKey: "item_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/item_name",
      },
      {
        header: "Warranty Date",
        accessorKey: "warranty_date",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Item Code",
        accessorKey: "item_code",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/item_code",
      },
      {
        header: "Item SKU",
        accessorKey: "item_sku",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/item_sku",
      },
      {
        header: "Serial No",
        accessorKey: "serial_no",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/serial_no",
      },
      {
        header: "Unit",
        accessorKey: "unit",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/unit",
      },
      {
        header: "Category",
        accessorKey: "category_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/category_name",
      },
      {
        header: "Sub Category",
        accessorKey: "subcategory_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/subcategory_name",
      },
      {
        header: "Make",
        accessorKey: "make_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/make_name",
      },
      {
        header: "Model",
        accessorKey: "model_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/model_name",
      },
      {
        header: "QC Status",
        accessorKey: "qc_pass",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const qcPass = row.original.qc_pass;

          const status = qcPass === "Pass" ? "Pass" : "Fail";
          const backgroundColor = qcPass === "Pass" ? "#0CBA91" : "#fb4a4aff";

          return (
            <span
              className="badge d-inline-flex align-items-center gap-2 text-uppercase"
              style={{
                borderRadius: "10px",
                backgroundColor,
                color: "white",
                fontWeight: 500,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  display: "inline-block",
                }}
              />
              {status}
            </span>
          );
        },
      },
      {
        header: "Remarks",
        accessorKey: "remarks",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/InStock/dropdown/remarks",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const item_id = row.original.item_id;

          return (
            <ul className="d-flex gap-2 list-unstyled mb-0">
              <li>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: "#3F7DDA", color: "#FFF" }}
                >
                  View Details
                </button>
              </li>
            </ul>
          );
        },
      },
    ],
    [dispatch, InStockItems]
  );

  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2 py-3">
                    <Col lg={3} className="me-auto d-flex align-items-center">
                      <i
                        className="bi bi-arrow-left me-2"
                        style={{ fontSize: "1.25rem", cursor: "pointer" }}
                        onClick={() =>
                          navigate("/warehouse/stock-overview/in-stock")
                        }
                      ></i>
                      <h6 className="card-title mb-0">
                        In Stock Items Details
                      </h6>
                    </Col>

                    <Col className="col-md-auto"></Col>
                  </Row>
                  <Row>
                    <div className="d-flex gap-3 mt-3">
                      <Link
                        to="/warehouse/stock-overview/in-stock"
                        style={{ textDecoration: "none" }}
                      ></Link>
                    </div>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  {loading ? (
                    <div className="text-center py-4">
                      <h5>Loading...</h5>
                    </div>
                  ) : error ? (
                    <div className="">
                      <WarehouseTableContainer
                        columns={columns}
                        data={data}
                        totalRecords={rowCount}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        onPageChange={setPageIndex}
                        onPageSizeChange={setPageSize}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        colValue={setFilterValue}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        isBordered={false}
                        isPagination={true}
                        isGlobalFilter={false}
                        SearchPlaceholder="Search In Stock Items..."
                        manualSorting={true}
                      />
                    </div>
                  ) : (
                    <WarehouseTableContainer
                      columns={columns}
                      data={data}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={setPageIndex}
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setFilterValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search In Stock Items..."
                      manualSorting={true}
                    />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default InStockItemsDetailsListings;
